import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import './NetPromoterScore.css';

export default function NetPromoterScore({ user, comp_id }) {
    const [loading, setLoading] = useState(true);
    const [npsData, setNpsData] = useState(null);
    const gaugeRef = useRef(null);
    const [gaugeSize, setGaugeSize] = useState(180);

    useEffect(() => {
        // TBD: Will be based on customer survey data
        // For now, render a placeholder with sample data
        const timer = setTimeout(() => {
            setNpsData({
                score: null, // No real data yet
                promoters: 0,
                passives: 0,
                detractors: 0,
                totalResponses: 0,
            });
            setLoading(false);
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                const minDim = Math.min(width, height);
                setGaugeSize(Math.max(minDim - 40, 100));
            }
        });
        if (gaugeRef.current) {
            resizeObserver.observe(gaugeRef.current);
        }
        return () => resizeObserver.disconnect();
    }, []);

    const getScoreColor = (score) => {
        if (score === null || score === undefined) return '#bdbdbd';
        if (score >= 50) return '#06D6A0';
        if (score >= 0) return '#FFD166';
        return '#EF476F';
    };

    const getScoreCategory = (score) => {
        if (score === null || score === undefined) return 'No Data';
        if (score >= 50) return 'Excellent';
        if (score >= 0) return 'Good';
        return 'Needs Improvement';
    };

    const hasData = npsData && npsData.score !== null;

    return (
        <div className="nps-container">
            <div className="nps-header">
                <span className="nps-header-title">
                    <i className="fas fa-star" style={{ color: '#FFD166' }} />
                    Net Promoter Score
                </span>
                <span className="nps-placeholder-badge">Survey TBD</span>
            </div>
            {loading ? (
                <div className="nps-loading">
                    <CircularProgress size={32} />
                </div>
            ) : !hasData ? (
                <div className="nps-body">
                    <div className="nps-gauge-wrapper" ref={gaugeRef}>
                        <Gauge
                            width={gaugeSize}
                            height={gaugeSize}
                            value={0}
                            startAngle={-110}
                            endAngle={110}
                            valueMin={-100}
                            valueMax={100}
                            sx={{
                                [`& .${gaugeClasses.valueText}`]: {
                                    fontSize: 28,
                                    fontWeight: 700,
                                    fill: '#bdbdbd',
                                },
                                [`& .${gaugeClasses.valueArc}`]: {
                                    fill: '#e0e0e0',
                                },
                                [`& .${gaugeClasses.referenceArc}`]: {
                                    fill: '#f5f5f5',
                                },
                            }}
                            text="—"
                        />
                        <span className="nps-score-subtitle">Awaiting survey data</span>
                    </div>
                </div>
            ) : (
                <>
                    <div className="nps-body">
                        <div className="nps-gauge-wrapper" ref={gaugeRef}>
                            <Gauge
                                width={gaugeSize}
                                height={gaugeSize}
                                value={npsData.score}
                                startAngle={-110}
                                endAngle={110}
                                valueMin={-100}
                                valueMax={100}
                                sx={{
                                    [`& .${gaugeClasses.valueText}`]: {
                                        fontSize: 32,
                                        fontWeight: 700,
                                        fill: getScoreColor(npsData.score),
                                    },
                                    [`& .${gaugeClasses.valueArc}`]: {
                                        fill: getScoreColor(npsData.score),
                                    },
                                    [`& .${gaugeClasses.referenceArc}`]: {
                                        fill: '#f0f0f0',
                                    },
                                }}
                            />
                            <span className="nps-score-subtitle">{getScoreCategory(npsData.score)}</span>
                        </div>
                    </div>
                    <div className="nps-breakdown">
                        <div className="nps-breakdown-item">
                            <span className="nps-breakdown-value" style={{ color: '#06D6A0' }}>{npsData.promoters}</span>
                            <span className="nps-breakdown-label">Promoters</span>
                        </div>
                        <div className="nps-breakdown-item">
                            <span className="nps-breakdown-value" style={{ color: '#FFD166' }}>{npsData.passives}</span>
                            <span className="nps-breakdown-label">Passives</span>
                        </div>
                        <div className="nps-breakdown-item">
                            <span className="nps-breakdown-value" style={{ color: '#EF476F' }}>{npsData.detractors}</span>
                            <span className="nps-breakdown-label">Detractors</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
